/**
 * CompanyPurchaseSuccess.jsx
 * ==========================
 * 
 * P0.2 — Page de confirmation après achat de Licences entreprise.
 * 
 * REFONTE COMPLÈTE:
 * - UX Premium lisible (mobile-first)
 * - Polling intelligent avec backoff
 * - États de synchronisation clairs
 * - CTA vers dashboard onglet licences
 */

import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { 
  CheckCircle2, 
  Building2, 
  Users, 
  ArrowRight, 
  Loader2,
  RefreshCw,
  Sparkles,
  CreditCard,
  Clock
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { safeFetchJSON } from '../utils/apiHelpers';
import { getBackendUrl } from '../config/environment';

const BACKEND_URL = getBackendUrl();

// Polling configuration
const POLL_INTERVAL_MS = 2000; // 2 secondes
const MAX_POLL_ATTEMPTS = 15; // 30 secondes max
const BACKOFF_MULTIPLIER = 1.2;

const CompanyPurchaseSuccess = () => {
  const navigate = useNavigate();
  const { companyId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  // États
  const [syncStatus, setSyncStatus] = useState('syncing'); // syncing, completed, failed
  const [company, setCompany] = useState(null);
  const [checkoutData, setCheckoutData] = useState(null);
  const [pollAttempt, setPollAttempt] = useState(0);
  const [manualSyncLoading, setManualSyncLoading] = useState(false);

  // Polling intelligent
  const checkStatus = useCallback(async () => {
    if (!sessionId) {
      setSyncStatus('failed');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const data = await safeFetchJSON(`${BACKEND_URL}/api/company/checkout-status/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (data.status === 'completed') {
        setCheckoutData(data);
        setSyncStatus('completed');
        // Fetch full company data
        const companyData = await safeFetchJSON(`${BACKEND_URL}/api/company/${data.company_id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        setCompany(companyData);
        toast.success('Paiement confirmé !');
        return true;
      }
      
      return false;
    } catch (error) {
      console.error('Error checking status:', error);
      return false;
    }
  }, [sessionId]);

  // Polling avec backoff
  useEffect(() => {
    if (syncStatus !== 'syncing' || !sessionId) return;

    const poll = async () => {
      const isComplete = await checkStatus();
      
      if (!isComplete && pollAttempt < MAX_POLL_ATTEMPTS) {
        const delay = POLL_INTERVAL_MS * Math.pow(BACKOFF_MULTIPLIER, Math.min(pollAttempt, 5));
        setTimeout(() => {
          setPollAttempt(prev => prev + 1);
        }, delay);
      } else if (!isComplete) {
        // 🔒 MOA (Jan 2026): Sync automatique avant abandon
        // Si le polling échoue après MAX_POLL_ATTEMPTS, tenter une sync directe avec Stripe
        console.log('[PURCHASE_SUCCESS] Polling failed, attempting automatic sync...');
        try {
          const token = localStorage.getItem('token');
          const syncResult = await safeFetchJSON(`${BACKEND_URL}/api/company/sync-checkout/${sessionId}`, {
            method: 'POST',
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (syncResult.status === 'synced' || syncResult.status === 'already_synced') {
            console.log('[PURCHASE_SUCCESS] Auto-sync successful, rechecking status...');
            const finalCheck = await checkStatus();
            if (!finalCheck) {
              setSyncStatus('failed');
            }
          } else {
            console.log('[PURCHASE_SUCCESS] Auto-sync failed:', syncResult.message);
            setSyncStatus('failed');
          }
        } catch (syncError) {
          console.error('[PURCHASE_SUCCESS] Auto-sync error:', syncError);
          setSyncStatus('failed');
        }
      }
    };

    poll();
  }, [pollAttempt, checkStatus, sessionId, syncStatus]);

  // Synchronisation manuelle
  const handleManualSync = async () => {
    setManualSyncLoading(true);
    try {
      const token = localStorage.getItem('token');
      const result = await safeFetchJSON(`${BACKEND_URL}/api/company/sync-checkout/${sessionId}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (result.status === 'synced' || result.status === 'already_synced') {
        await checkStatus();
        toast.success('Synchronisation réussie !');
      } else {
        toast.error(result.message || 'Erreur de synchronisation');
      }
    } catch (error) {
      toast.error('Erreur lors de la synchronisation');
    } finally {
      setManualSyncLoading(false);
    }
  };

  // Navigation vers dashboard licences
  const goToLicenseManagement = () => {
    navigate('/user-dashboard?tab=entreprise');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900 to-purple-900 flex items-center justify-center p-4 md:p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-xl w-full"
      >
        {/* Card principale */}
        <div className="bg-white/10 backdrop-blur-xl rounded-3xl p-6 md:p-10 border border-white/20 shadow-2xl">
          
          {/* === ÉTAT: SYNCHRONISATION EN COURS === */}
          <AnimatePresence mode="wait">
            {syncStatus === 'syncing' && (
              <motion.div
                key="syncing"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="text-center"
              >
                {/* Icône animée */}
                <div className="w-24 h-24 mx-auto mb-8 relative">
                  <div className="absolute inset-0 rounded-full bg-indigo-500/20 animate-ping" />
                  <div className="relative w-full h-full rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center">
                    <Loader2 className="w-12 h-12 text-white animate-spin" />
                  </div>
                </div>
                
                <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                  Confirmation en cours...
                </h1>
                <p className="text-gray-300 mb-6">
                  Nous vérifions votre paiement auprès de Stripe
                </p>
                
                {/* Progress indicator */}
                <div className="flex items-center justify-center gap-2 text-indigo-300 text-sm">
                  <Clock className="w-4 h-4" />
                  <span>Tentative {pollAttempt + 1} / {MAX_POLL_ATTEMPTS}</span>
                </div>
                
                {/* Barre de progression */}
                <div className="mt-4 h-1 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full bg-gradient-to-r from-indigo-500 to-purple-500"
                    initial={{ width: '0%' }}
                    animate={{ width: `${((pollAttempt + 1) / MAX_POLL_ATTEMPTS) * 100}%` }}
                    transition={{ duration: 0.3 }}
                  />
                </div>
              </motion.div>
            )}

            {/* === ÉTAT: SUCCÈS === */}
            {syncStatus === 'completed' && (
              <motion.div
                key="completed"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                {/* Success Icon */}
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ type: "spring", delay: 0.1 }}
                  className="w-24 h-24 mx-auto mb-8 rounded-full bg-gradient-to-br from-emerald-400 to-green-600 flex items-center justify-center shadow-lg shadow-emerald-500/30"
                >
                  <CheckCircle2 className="w-14 h-14 text-white" />
                </motion.div>
                
                <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
                  Paiement Confirmé !
                </h1>
                <p className="text-gray-300 mb-8 flex items-center justify-center gap-2">
                  <Sparkles className="w-5 h-5 text-amber-400" />
                  Vos Licences sont maintenant disponibles
                </p>
                
                {/* Récapitulatif */}
                <div className="bg-white/5 rounded-2xl p-6 mb-8 text-left border border-white/10">
                  {/* En-tête entreprise */}
                  <div className="flex items-center gap-4 pb-4 border-b border-white/10 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-500/20 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-bold text-lg">
                        {checkoutData?.company_name || company?.name}
                      </p>
                      <p className="text-gray-400 text-sm">{company?.billing_email}</p>
                    </div>
                  </div>
                  
                  {/* Stats Licences */}
                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div className="bg-emerald-500/10 rounded-xl p-4 border border-emerald-500/20">
                      <p className="text-emerald-300 text-xs font-medium mb-1">Licences achetés</p>
                      <p className="text-3xl font-bold text-emerald-400">
                        {checkoutData?.Licences_purchased || company?.Licences_purchased || 0}
                      </p>
                    </div>
                    <div className="bg-purple-500/10 rounded-xl p-4 border border-purple-500/20">
                      <p className="text-purple-300 text-xs font-medium mb-1">Disponibles</p>
                      <p className="text-3xl font-bold text-purple-400">
                        {checkoutData?.Licences_available || company?.Licences_available || 0}
                      </p>
                    </div>
                  </div>
                  
                  {/* Montant */}
                  {checkoutData?.amount_paid && (
                    <div className="flex items-center justify-between p-3 bg-white/5 rounded-xl">
                      <span className="text-gray-400 flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        Montant payé
                      </span>
                      <span className="text-white font-bold">
                        {checkoutData.amount_paid.toFixed(2)} {checkoutData.currency}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* Actions */}
                <div className="space-y-3">
                  <button
                    onClick={goToLicenseManagement}
                    data-testid="manage-licenses-btn"
                    className="w-full px-6 py-4 bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700 text-white font-bold rounded-xl flex items-center justify-center gap-3 transition-all shadow-lg shadow-purple-500/20 hover:shadow-purple-500/40"
                  >
                    <Users className="w-5 h-5" />
                    Gérer mes licences
                    <ArrowRight className="w-5 h-5" />
                  </button>
                  
                  <button
                    onClick={() => navigate('/training')}
                    data-testid="go-to-training-btn"
                    className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    Accéder à la plateforme
                  </button>
                </div>
                
                {/* Référence */}
                {sessionId && (
                  <p className="text-gray-500 text-xs mt-6">
                    Réf. transaction : {sessionId.slice(0, 24)}...
                  </p>
                )}
              </motion.div>
            )}

            {/* === ÉTAT: ÉCHEC === */}
            {syncStatus === 'failed' && (
              <motion.div
                key="failed"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-center"
              >
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-amber-500/20 flex items-center justify-center">
                  <RefreshCw className="w-10 h-10 text-amber-400" />
                </div>
                
                <h1 className="text-2xl font-bold text-white mb-2">
                  Synchronisation en attente
                </h1>
                <p className="text-gray-300 mb-6">
                  Le paiement est confirmé mais la synchronisation prend plus de temps que prévu.
                </p>
                
                <div className="space-y-3">
                  <button
                    onClick={handleManualSync}
                    disabled={manualSyncLoading}
                    data-testid="manual-sync-btn"
                    className="w-full px-6 py-4 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-bold rounded-xl flex items-center justify-center gap-2 transition-all"
                  >
                    {manualSyncLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Synchronisation...
                      </>
                    ) : (
                      <>
                        <RefreshCw className="w-5 h-5" />
                        Synchroniser manuellement
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => {
                      setSyncStatus('syncing');
                      setPollAttempt(0);
                    }}
                    className="w-full px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/20 text-white font-medium rounded-xl transition-colors"
                  >
                    Réessayer automatiquement
                  </button>
                </div>
                
                <p className="text-gray-500 text-sm mt-6">
                  Si le problème persiste, contactez le support avec la référence :<br/>
                  <code className="text-xs bg-white/10 px-2 py-1 rounded mt-1 inline-block">
                    {sessionId}
                  </code>
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        
        {/* Footer email */}
        {syncStatus === 'completed' && (
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.5 }}
            className="text-center text-gray-400 text-sm mt-6"
          >
            📧 Un email de confirmation a été envoyé à l'adresse de facturation
          </motion.p>
        )}
      </motion.div>
    </div>
  );
};

export default CompanyPurchaseSuccess;
